import classnames from 'classnames';
import Icon from 'components/Icon/Icon';
import { forwardRef, useMemo } from 'react';
import useIntl from 'utils/useIntl';

import styles from './DatePicker.module.scss';

type DatePickerButtonProps = Partial<{
  value: string | undefined;
  onClick: () => void;
  isActive: boolean;
}>;

/**
 * Converts a string in the format 'dd/mm/yyyy' to a Date object.
 *
 * @param {string} value - The date string in the format 'dd/mm/yyyy'.
 * @returns {Date} - A Date object corresponding to the given date.
 *
 * @example
 * const date = getDateFromString('12/03/2025');
 * console.log(date); // Wed Mar 12 2025 00:00:00 GMT+0000 (Coordinated Universal Time)
 */
const getDateFromString = (value: string): Date => {
  const delimiter = '/';

  const [day, month, year] = value
    .split(delimiter)
    .map((part) => Number.parseInt(part, 10));

  return new Date(year, month - 1, day);
};

const DatePickerButton = forwardRef<HTMLButtonElement, DatePickerButtonProps>(
  ({ value, onClick, isActive }, ref) => {
    const { formatDate } = useIntl();

    const formattedDate = useMemo(() => {
      if (value) {
        return formatDate(getDateFromString(value));
      }

      return '';
    }, [value, formatDate]);

    return (
      <button
        type="button"
        onClick={onClick}
        className={styles['datepicker-button']}
        ref={ref}
      >
        {formattedDate}
        <Icon
          name="calendar"
          className={classnames(styles['calendar-icon'], {
            [styles['calendar-icon-active']]: isActive,
          })}
        />
      </button>
    );
  },
);

export default DatePickerButton;
